import React, { ReactNode, useCallback, useMemo, useRef } from 'react';
import {
    firstIndexToFocus,
    ListRow,
    RowIsFocusable,
    RowKey,
    uniqueRowKeys,
} from 'flow/components/nav/list-keys/list-key-action';
import { RowStore } from 'flow/components/nav/list-keys/row-index-store';
import useListTabStop from 'flow/components/nav/list-keys/useListTabStop';
import DateSectionHeader from 'flow/components/pbx/v2/detail-page/call-event/DateSectionHeader';

export interface DateRows<Row> {
    date: string;
    rows: Row[];
}

/** What a row renders its ListRowFrame with. */
export interface DateGroupedRowProps {
    index: number;
    tabStopStore: RowStore<string>;
    tabStopKey: string;
    onUnmountWithFocus: () => void;
}

interface Props<Row extends ListRow> {
    dates: DateRows<Row>[];
    rowKey: RowKey<Row>;
    rowIsFocusable: RowIsFocusable<Row>;
    /**
     * The row that Tab enters the list at until a row has had focus; the
     * first focusable row when not given.
     */
    entryIndex?: (rows: Row[]) => number;
    /** The row's content, inside the list's own `li`. */
    renderRow: (row: Row, rowProps: DateGroupedRowProps) => ReactNode;
    bgClassName?: string;
}

/**
 * Rows by date, as one Tab stop: the row that last had focus holds it, else
 * the entry row. Up, Down, Home and End move between the rows, across the
 * dates. The list counts its rows through all the dates, as the Tab stop and
 * the arrow keys do.
 */
const DateGroupedList = <Row extends ListRow>({
    dates,
    rowKey,
    rowIsFocusable,
    entryIndex,
    renderRow,
    bgClassName,
}: Props<Row>) => {
    const containerRef = useRef<HTMLOListElement>(null);
    const rows = useMemo(() => dates.flatMap((date) => date.rows), [dates]);
    const rowKeys = useMemo(() => uniqueRowKeys(rows, rowKey), [rows, rowKey]);

    const entryIndexOfRows = useCallback(
        () =>
            entryIndex
                ? entryIndex(rows)
                : firstIndexToFocus(rows, rowIsFocusable),
        [rows, entryIndex, rowIsFocusable],
    );

    const { tabStopStore, onFocus, onKeyDown, onRowUnmountWithFocus } =
        useListTabStop({
            containerRef,
            rows,
            rowKeys,
            rowIsFocusable,
            entryIndex: entryIndexOfRows,
        });

    let dateFirstIndex = 0;
    return (
        <ol // NOSONAR
            // Not a control: see useListTabStop.
            ref={containerRef}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
        >
            {dates.map(({ date, rows: dateRows }) => {
                const firstIndex = dateFirstIndex;
                dateFirstIndex += dateRows.length;
                return (
                    // The date title sticks over the top of the scroller. So
                    // a control that gets focus scrolls into view below it.
                    <li key={date} className="**:scroll-mt-9">
                        <DateSectionHeader
                            date={date}
                            bgClassName={bgClassName}
                        />
                        <ol>
                            {dateRows.map((row, offset) => {
                                const index = firstIndex + offset;
                                return (
                                    <li key={rowKeys[index]}>
                                        {renderRow(row, {
                                            index,
                                            tabStopStore,
                                            tabStopKey: rowKeys[index],
                                            onUnmountWithFocus:
                                                onRowUnmountWithFocus,
                                        })}
                                    </li>
                                );
                            })}
                        </ol>
                    </li>
                );
            })}
        </ol>
    );
};

export default DateGroupedList;
